import {
  Card,
  Control,
  Language,
  Notification,
  Player,
  Pointer,
  View,
} from '@yleisradio/areena-types';
import { useCards } from 'hooks/useCards';
import { useUILanguage } from 'hooks/useUILanguage';
import { getChannelView } from 'services/areena-api/fetchers';
import useSWR from 'swr';
import { isList } from 'utils/content';

type FetcherParams = ['schedule', channelId: string, language: Language];

function fetcher([, channelId, language]: FetcherParams) {
  return getChannelView(channelId, language);
}

function getScheduleListPointer(view: View | undefined): Pointer | undefined {
  const scheduleList = view?.tabs?.[0]?.content?.[0];

  return isList(scheduleList) ? scheduleList.source : undefined;
}

export function useScheduleData(channelId: string): {
  channelLinks: Control[];
  channelPlayer: Player | undefined;
  currentProgram: Card | undefined;
  upcomingPrograms: Card[];
  errored: boolean;
  notifications: Notification[];
} {
  const language = useUILanguage();

  const { data: channelViewResponse, error: channelViewError } = useSWR(
    ['schedule', channelId, language] satisfies FetcherParams,
    fetcher
  );

  const viewNotifications = channelViewResponse?.notifications || [];
  const channelLinks = channelViewResponse?.data?.header?.controls || [];
  const channelPlayer = channelViewResponse?.data?.players?.live;
  const scheduleListPointer = getScheduleListPointer(channelViewResponse?.data);

  const {
    cards,
    errored: hasListFetchErrored,
    notifications: listNotifications = [],
  } = useCards({
    source: scheduleListPointer,
    pageIndex: 0,
    pageSize: 3,
    refreshInterval: 60_000,
    revalidateOnFocus: true,
  });

  return {
    channelLinks,
    channelPlayer,
    currentProgram: cards[0],
    upcomingPrograms: cards.slice(1),
    errored: !!channelViewError || hasListFetchErrored,
    notifications: [...viewNotifications, ...listNotifications],
  };
}
